/**
 * Created by vwiny on 10/10/2017.
 */
import { Injectable, ViewContainerRef } from '@angular/core';
import { SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMReportPersonalizationDialogComponent } from './lm-report-personalization-dialog.component';
import { Observable, Subject } from 'rxjs';
import { LMDialogButtonKey, LMUniqueIdService } from '@utils/index';

export enum LMReportPersonalizationDialogResultCode {
  REPORT_PERSONALIZATION_DIALOG_CANCEL = 0,
  REPORT_PERSONALIZATION_DIALOG_OK,
  REPORT_PERSONALIZATION_DIALOG_RESET,
  REPORT_PERSONALIZATION_DIALOG_SAVED
}

/**
 * Must be a class and not an interface for typescript type guards to work.
 */
export class LMReportPersonalizationDialogResult {
  status: LMReportPersonalizationDialogResultCode;
  displayFields?: any[];
  reportDataView?: LMReportPersonalizationDataView;
  message?: string;
  viewReport?: boolean;
  reportUrl?: string;
}

@Injectable()
export class LMReportPersonalizationDialogService {
  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmLoggerService: LMLoggerService,
    private lmUniqueIdService: LMUniqueIdService) {
  }

  public dialog: SohoModalDialogRef<any>;

  public runReportPersonalizationDialog(
    mode: LMReportPersonalizationMode,
    placeholder: ViewContainerRef,
    url?: string,
    actorGroupSelectUrl?: string,
    roleSelectUrl?: string,
    roleGroupSelectUrl?: string,
    fieldSearchUrl?: string,
    listTitle?: string,
    listUrlInfo?: LMListUrlInfo,
    listSpec?: LMListSpec,
    dataViewSet?: LMDataViewSet,
    personalizationExists?: boolean,
    includeBaseFilter?: boolean,
    filterCondition?: string,
    chartName?: string
  ): Observable<LMReportPersonalizationDialogResult> {
    return new Observable<LMReportPersonalizationDialogResult>(observer => {
      this.lmLoggerService.log([ 'LMReportPersonalizationDialog', 'formService.getFormSpec()', 'Complete' ]);

      const reportPersonalizationDialog$: Subject<LMReportPersonalizationDialogResult> = new Subject<LMReportPersonalizationDialogResult>();
      let dlgComponent: LMReportPersonalizationDialogComponent;
      const dialogId = 'ReportPersonalization';
      const okButtonKey = (mode === 'createReport' && !personalizationExists) ? LMDialogButtonKey.CREATEREPORT : LMDialogButtonKey.OK;

      this.lmBusyIndicatorService.hideBusyIndicator();

      const buttons: SohoModalButton[] = [
        // TODO: The button default disables the button. A bug in controls?
        // tslint:disable-next-line - shadowed variable e
        {
          id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            reportPersonalizationDialog$.complete();
            this.dialog.close(<LMReportPersonalizationDialogResult>{
              status: LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_CANCEL
            });
          }
        }
      ];

      if (!personalizationExists && mode === 'createReport' || mode === 'report') {
        buttons.push(
          {
            id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CREATEANDVIEWREPORT ]),
            text:  Soho.Locale.translate(LMDialogButtonKey.CREATEANDVIEWREPORT),
            isDefault: true,
            click: (e, modal) => {
              reportPersonalizationDialog$.next(<LMReportPersonalizationDialogResult>{
                status:         LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_OK,
                reportDataView: dlgComponent.dataView,
                viewReport:     true
              });
            }
          }
        );
      }

      if (personalizationExists) {
        buttons.push(
          {
            id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.RESET ]),
            text:  Soho.Locale.translate(LMDialogButtonKey.RESET),
            click: (e, modal) => {
              reportPersonalizationDialog$.next(<LMReportPersonalizationDialogResult>{
                status:         LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_RESET,
                reportDataView: dlgComponent.dataView
              });
            }
          }
        );
      }

      buttons.push(
        {
          id:        this.lmUniqueIdService.uniqueId([ dialogId, okButtonKey ]),
          text:      Soho.Locale.translate(okButtonKey),
          isDefault: true,
          click:     (e, modal) => {
            reportPersonalizationDialog$.next(<LMReportPersonalizationDialogResult>{
              status:         LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_OK,
              reportDataView: dlgComponent.dataView
            });
          }
        }
      );

      const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-report-personalization-modal' } ];
      const options: SohoModalOptions = {
        fullsize: 'always',
        attributes
      };


      this.dialog = this.sohoModalDialogService
        .modal(LMReportPersonalizationDialogComponent, placeholder, options)
        .title(mode === 'report' || mode === 'createReport' ? Soho.Locale.translate('CreateReport') : Soho.Locale.translate('Personalize'))
        .buttons(buttons)
        .isAlert(false)
        .open()
        .apply((ReportPersonalization: LMReportPersonalizationDialogComponent) => {
          dlgComponent = ReportPersonalization;
          dlgComponent.mode = mode;
          dlgComponent.personalizationExists = personalizationExists;

          if (url) {
            dlgComponent.url = url;
          }
          if (actorGroupSelectUrl) {
            dlgComponent.actorGroupUrl = actorGroupSelectUrl;
          }
          if (roleSelectUrl) {
            dlgComponent.roleUrl = roleSelectUrl;
          }
          if (roleGroupSelectUrl) {
            dlgComponent.roleGroupUrl = roleGroupSelectUrl;
          }
          if (fieldSearchUrl) {
            dlgComponent.fieldSearchUrl = fieldSearchUrl;
          }
          if (listSpec) {
            dlgComponent.listTitle = listTitle;
            dlgComponent.listUrlInfo = listUrlInfo;
            dlgComponent.listSpec = listSpec;
            dlgComponent.dataViewSet = dataViewSet;
            dlgComponent.includeBaseFilter = includeBaseFilter;
            dlgComponent.filterCondition = filterCondition;

            if(chartName) {
              dlgComponent.chartName = chartName;
            }
          }

          dlgComponent.dialog$ = reportPersonalizationDialog$;
        })
        .afterClose(result => {
          observer.next(result);
          observer.complete();
        });

      reportPersonalizationDialog$.subscribe((result: LMReportPersonalizationDialogResult) => {
        if (result.status === LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_SAVED) {
          reportPersonalizationDialog$.complete();
          this.dialog.close(result);
        }
      });
    });
  }
}
